/**
 * 🎨 CSS Variables Dynamic Engine
 * Sistema de variables CSS dinámicas para temas unificados
 * 
 * @description Motor que aplica variables CSS en tiempo real para cambios
 * instantáneos de tema sin re-renders de React
 */

/**
 * Aplica las variables CSS del tema al documento
 * @param {Object} themeConfig - Configuración completa del tema
 */
export const applyThemeVariables = (themeConfig) => {
  const root = document.documentElement;
  
  // 🎨 Colores primarios y secundarios
  if (themeConfig.palette?.primary) {
    root.style.setProperty('--theme-primary-main', themeConfig.palette.primary.main);
    root.style.setProperty('--theme-primary-light', themeConfig.palette.primary.light);
    root.style.setProperty('--theme-primary-dark', themeConfig.palette.primary.dark);
    root.style.setProperty('--theme-primary-contrast', themeConfig.palette.primary.contrastText);
  }

  if (themeConfig.palette?.secondary) {
    root.style.setProperty('--theme-secondary-main', themeConfig.palette.secondary.main);
    root.style.setProperty('--theme-secondary-light', themeConfig.palette.secondary.light);
    root.style.setProperty('--theme-secondary-dark', themeConfig.palette.secondary.dark);
  }

  // 🖼️ Fondos y superficies
  if (themeConfig.palette?.background) {
    root.style.setProperty('--theme-bg-default', themeConfig.palette.background.default);
    root.style.setProperty('--theme-bg-paper', themeConfig.palette.background.paper);
    
    // ✅ ARREGLO: Aplicar fondo directamente al body
    document.body.style.backgroundColor = themeConfig.palette.background.default;
    document.documentElement.style.backgroundColor = themeConfig.palette.background.default;
  }

  // 📝 Textos
  if (themeConfig.palette?.text) {
    root.style.setProperty('--theme-text-primary', themeConfig.palette.text.primary);
    root.style.setProperty('--theme-text-secondary', themeConfig.palette.text.secondary);
    root.style.setProperty('--theme-text-disabled', themeConfig.palette.text.disabled);
  }

  // 🔲 Divisores y bordes
  if (themeConfig.palette?.divider) {
    root.style.setProperty('--theme-divider', themeConfig.palette.divider);
  } else {
    // Fallback basado en el modo del tema
    const dividerColor = themeConfig.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.12)' 
      : 'rgba(0, 0, 0, 0.12)';
    root.style.setProperty('--theme-divider', dividerColor);
  }

  // 🎯 Variables específicas por sección
  if (themeConfig.sections) {
    // 💬 Chat
    if (themeConfig.sections.chat) {
      root.style.setProperty('--chat-bg', themeConfig.sections.chat.background);
      root.style.setProperty('--chat-message-bg', themeConfig.sections.chat.messageBg);
      root.style.setProperty('--chat-input-bg', themeConfig.sections.chat.inputBg);
    }

    // 🎛️ Editor de Flujos
    if (themeConfig.sections.editor) {
      root.style.setProperty('--editor-canvas-bg', themeConfig.sections.editor.canvasBg);
      root.style.setProperty('--editor-node-bg', themeConfig.sections.editor.nodeBg);
      root.style.setProperty('--editor-node-border', themeConfig.sections.editor.nodeBorder);
      root.style.setProperty('--editor-toolbar-bg', themeConfig.sections.editor.toolbarBg);
      // `gridColor` NO se publica aquí a propósito: lo consume FlowCanvas por JS
      // (`theme.sections.editor.gridColor`), porque acaba en el `fill` de un <circle> SVG
      // y un `var()` sin resolver pintaría la retícula de negro. Una variable CSS que
      // nadie lee es justo el bug que arregla #676.
    }

    // 👥 Editor de Roles
    if (themeConfig.sections.roles) {
      root.style.setProperty('--roles-card-bg', themeConfig.sections.roles.cardBg);
      root.style.setProperty('--roles-sidebar-bg', themeConfig.sections.roles.sidebarBg);
      root.style.setProperty('--roles-accent', themeConfig.sections.roles.accent);
    }

    // ⚙️ Settings
    if (themeConfig.sections.settings) {
      root.style.setProperty('--settings-panel-bg', themeConfig.sections.settings.panelBg);
      root.style.setProperty('--settings-card-bg', themeConfig.sections.settings.cardBg);
    }
  }

  // ✨ Efectos y sombras
  if (themeConfig.shadows) {
    root.style.setProperty('--theme-shadow-sm', themeConfig.shadows.sm);
    root.style.setProperty('--theme-shadow-md', themeConfig.shadows.md);
    root.style.setProperty('--theme-shadow-lg', themeConfig.shadows.lg);
    root.style.setProperty('--theme-shadow-xl', themeConfig.shadows.xl);
  }

  // 📏 Espaciado
  if (themeConfig.spacing) {
    root.style.setProperty('--theme-spacing-xs', themeConfig.spacing.xs);
    root.style.setProperty('--theme-spacing-sm', themeConfig.spacing.sm);
    root.style.setProperty('--theme-spacing-md', themeConfig.spacing.md);
    root.style.setProperty('--theme-spacing-lg', themeConfig.spacing.lg);
    root.style.setProperty('--theme-spacing-xl', themeConfig.spacing.xl);
  }

  // 🎭 Animaciones
  if (themeConfig.animations) {
    root.style.setProperty('--theme-transition-fast', themeConfig.animations.fast);
    root.style.setProperty('--theme-transition-normal', themeConfig.animations.normal);
    root.style.setProperty('--theme-transition-slow', themeConfig.animations.slow);
    root.style.setProperty('--theme-easing', themeConfig.animations.easing);
  }

  console.log(`🎨 [ThemeEngine] Variables CSS aplicadas para tema: ${themeConfig.name || 'Unknown'}`);
};

/**
 * Obtiene el valor actual de una variable CSS
 * @param {string} variable - Nombre de la variable (sin --)
 * @returns {string} Valor de la variable
 */
export const getCSSVariable = (variable) => {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(`--${variable}`)
    .trim();
};

/**
 * Elimina todas las variables de tema (para reset)
 */
export const clearThemeVariables = () => {
  const root = document.documentElement;
  const style = root.style;
  
  // Lista de todas las variables que manejamos
  const themeVariables = [
    'theme-primary-main', 'theme-primary-light', 'theme-primary-dark', 'theme-primary-contrast',
    'theme-secondary-main', 'theme-secondary-light', 'theme-secondary-dark',
    'theme-bg-default', 'theme-bg-paper',
    'theme-text-primary', 'theme-text-secondary', 'theme-text-disabled',
    'chat-bg', 'chat-message-bg', 'chat-input-bg',
    'editor-canvas-bg', 'editor-node-bg', 'editor-node-border', 'editor-toolbar-bg',
    'roles-card-bg', 'roles-sidebar-bg', 'roles-accent',
    'settings-panel-bg', 'settings-card-bg',
    'theme-shadow-sm', 'theme-shadow-md', 'theme-shadow-lg', 'theme-shadow-xl',
    'theme-spacing-xs', 'theme-spacing-sm', 'theme-spacing-md', 'theme-spacing-lg', 'theme-spacing-xl',
    'theme-transition-fast', 'theme-transition-normal', 'theme-transition-slow', 'theme-easing'
  ];

  themeVariables.forEach(variable => {
    style.removeProperty(`--${variable}`);
  });

  console.log('🧹 [ThemeEngine] Variables CSS limpiadas');
};

/**
 * Detecta si el usuario prefiere modo oscuro
 * @returns {boolean}
 */
export const detectDarkMode = () => {
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
};

/**
 * Observa cambios en la preferencia de color del sistema
 * @param {Function} callback - Función que se ejecuta al cambiar
 */
export const watchSystemTheme = (callback) => {
  if (window.matchMedia) {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addListener(callback);
    return () => mediaQuery.removeListener(callback);
  }
  return () => {};
};
